import { useEffect, useState } from "react";
import { ChevronDown, Leaf, FlaskConical, HeartPulse } from "lucide-react";
import { api, assetUrl } from "@/lib/api";

const icons = [Leaf, FlaskConical, HeartPulse];

export default function AboutHomeopathy() {
  const [c, setC] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/content/about_homeopathy").then(({ data }) => setC(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-homeopathy-page">
      <section className="relative overflow-hidden border-b border-border bg-secondary/40">
        <div className="container-xl grid items-center gap-8 py-14 md:py-20 lg:grid-cols-2">
          <div>
            <span className="eyebrow text-accent">About Homeopathy</span>
            <h1 className="mt-3 text-4xl font-700 sm:text-5xl">{c?.heading || "Understanding Homeopathy"}</h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">{c?.intro}</p>
          </div>
          <div className="mx-auto w-full max-w-[420px] overflow-hidden rounded-[2rem] shadow-xl ring-1 ring-border">
            <img
              src={assetUrl(c?.image) || "https://images.unsplash.com/photo-1514733670139-4d87a1941d55?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"}
              alt="Herbal infusion"
              className="h-[450px] w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-xl">
          <span className="eyebrow text-accent">Core Principles</span>
          <h2 className="mt-3 text-3xl font-700 sm:text-4xl">The foundations of healing</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {(c?.principles || []).map((p, i) => {
              const Icon = icons[i % icons.length];
              return (
                <div key={i} className="rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:shadow-lg animate-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`homeopathy-principle-${i}`}>
                  <div className="inline-flex rounded-xl bg-accent/15 p-3 text-accent"><Icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 font-serif text-xl text-[var(--brand-navy)]">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad bg-secondary/40">
        <div className="container-xl max-w-3xl">
          <span className="eyebrow text-accent">FAQ</span>
          <h2 className="mt-3 text-3xl font-700 sm:text-4xl">Frequently asked questions</h2>
          <div className="mt-8 space-y-3">
            {(c?.faqs || []).map((f, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`homeopathy-faq-${i}`}>
                <button
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`homeopathy-faq-toggle-${i}`}
                >
                  <span className="font-600 text-foreground">{f.q}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-primary transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
